
import { BehaviorSubject, Observable } from 'rxjs';
import { Inject, Injectable, DOCUMENT } from '@angular/core';

/**
 * interfaces
 */
import { ILayoutConfig } from './interfaces/ILayoutConfig';

/**
 * enums
 */
import { IThemeSkin, ThemeSkin } from '@core/enums/theme-skin';
import { ILayoutDirection, ILayoutLang, ILayoutMode, LayoutDirection, LayoutLang, LayoutMode } from './enums/layout-config';

/**
 * @description LayoutService manages the application's layout configuration including theme modes, 
 * language settings, RTL support, menu states, and skin preferences. It provides a centralized 
 * way to control and persist layout settings across the application.
 * 
 * @example
 * ```typescript
 * // Inject the service
 * constructor(private layoutService: LayoutService) {}
 * 
 * // Toggle dark mode
 * this.layoutService.toggleDarkMode();
 * 
 * // Subscribe to layout changes
 * this.layoutService.layoutConfig$.subscribe(config => {
 *   console.log('Layout changed:', config);
 * });
 * ```
 */
@Injectable()
export class LayoutService {

  /**
   * @description Default configuration for theme layout. If localStorage has a value, it will return that;
   * otherwise, it returns the current default configuration. This ensures persistence across sessions.
   * 
   * @private
   * @type {ILayoutConfig}
   * @default
   * - isRtl: false (Left-to-right text direction)
   * - isDark: false (Light theme mode)
   * - lang: LayoutLang.En (English language)
   * - menuCollapsed: false (Menu expanded by default)
   * - enabledSemiDark: true (Semi-dark theme enabled)
   * - skin: ThemeSkin.Bordered (Bordered skin style)
   */
  private _defaultLayoutConfig: ILayoutConfig  = JSON.parse(localStorage.getItem('layoutConfig') as string) ?? {
    isRtl: false,
    isDark: false,
    lang: LayoutLang.En,
    menuCollapsed: false,
    enabledSemiDark: true,
    skin: ThemeSkin.Bordered
  }

  /**
   * @description BehaviorSubject that holds the current layout configuration and emits changes
   * to all subscribers when the configuration is updated.
   * 
   * @private
   * @type {BehaviorSubject<ILayoutConfig>}
   */
  private _layoutConfig: BehaviorSubject<ILayoutConfig> = new BehaviorSubject<ILayoutConfig>(this._defaultLayoutConfig);

  /**
   * @description Getter for the current layout configuration value (synchronous access).
   * 
   * @returns {ILayoutConfig} The current layout configuration object
   * 
   * @example
   * ```typescript
   * const currentConfig = this.layoutService.layoutConfigValue;
   * console.log('Current theme:', currentConfig.isDark ? 'Dark' : 'Light');
   * ```
   */
  public get layoutConfigValue(): ILayoutConfig {
    return this._layoutConfig.value;
  }

  /**
   * @description Constructor initializes the LayoutService and sets up the initial configuration.
   * It injects the Document object to manipulate DOM attributes and calls _initConfig() to
   * apply the current configuration to the DOM.
   * 
   * @param {Document} _document - Angular's injected document object for DOM manipulation
   * 
   * @example
   * ```typescript
   * // Service is automatically injected by Angular's DI container
   * constructor(private layoutService: LayoutService) {}
   * ```
   */
  constructor(@Inject(DOCUMENT)private _document: Document) {
    this._initConfig();
  }

  /**
   * @description Observable that emits the current layout configuration whenever it changes.
   * Components can subscribe to this observable to react to layout changes in real-time.
   * 
   * @returns {Observable<ILayoutConfig>} Observable stream of layout configuration changes
   * 
   * @example
   * ```typescript
   * this.layoutService.layoutConfig$.subscribe(config => {
   *   this.updateTheme(config.isDark);
   *   this.updateLanguage(config.lang);
   * });
   * ```
   */
  public get layoutConfig$(): Observable<ILayoutConfig> {
    return this._layoutConfig.asObservable();
  }

  /**
   * @description Getter for the current dark mode status.
   * 
   * @returns {boolean} True if dark mode is enabled, false otherwise
   * 
   * @example
   * ```typescript
   * if (this.layoutService.darkMode) {
   *   console.log('Dark mode is active');
   * }
   * ```
   */
  public get darkMode(): boolean {
    return this._layoutConfig.value.isDark ? true : false;
  }

  /**
   * @description Setter for dark mode. Enables or disables dark theme mode. The setter includes
   * validation to prevent unnecessary updates if the value is the same as the current state.
   * 
   * @param {boolean} isDark - True to enable dark mode, false to disable
   * 
   * @example
   * ```typescript
   * // Enable dark mode
   * this.layoutService.darkMode = true;
   * 
   * // Disable dark mode
   * this.layoutService.darkMode = false;
   * ```
   */
  public set darkMode(isDark: boolean) {

    // ES6 Destruction
    const { value: { isDark: currentDarkModeStatus} } = this._layoutConfig;

    if (typeof isDark != "boolean" || isDark == currentDarkModeStatus) {
      return;
    }

    this.storeLayoutConfigChanges({isDark});

  }

  /**
   * @description Toggles the current dark mode status. If dark mode is currently enabled,
   * it will be disabled, and vice versa. This method automatically saves changes to localStorage.
   * 
   * @example
   * ```typescript
   * // Toggle between light and dark themes
   * this.layoutService.toggleDarkMode();
   * ```
   */
  public toggleDarkMode(): void {

    // toggle dark mode
    this.darkMode = !this.darkMode;

    // update layout config
    // save changes in localStorage
    this.saveLayoutConfigChanges();

  }

  /**
   * @description Getter for the current semi-dark mode status.
   * 
   * @returns {boolean} True if semi-dark mode is enabled, false otherwise
   * 
   * @example
   * ```typescript
   * if (this.layoutService.enabledSemiDark) {
   *   console.log('Semi-dark mode is active');
   * }
   * ```
   */
  public get enabledSemiDark(): boolean {
    return this._layoutConfig.value.enabledSemiDark ? true : false;
  }

  /**
   * @description Setter for semi-dark mode. Enables or disables the semi-dark theme variant.
   * Includes validation to prevent unnecessary updates.
   * 
   * @param {boolean} enabledSemiDark - True to enable semi-dark mode, false to disable
   * 
   * @example
   * ```typescript
   * // Enable semi-dark mode
   * this.layoutService.enabledSemiDark = true;
   * ```
   */
  public set enabledSemiDark(enabledSemiDark: boolean) {

    // ES6 Destruction
    const { value: { enabledSemiDark: currentSemiDarkStatus} } = this._layoutConfig;

    if (typeof enabledSemiDark != "boolean" || enabledSemiDark == currentSemiDarkStatus) {
      return;
    }

    this.storeLayoutConfigChanges({enabledSemiDark});

  }

  /**
   * @description Getter for the current theme skin.
   * 
   * @returns {IThemeSkin} The current theme skin (e.g., 'default', 'bordered')
   * 
   * @example
   * ```typescript
   * const currentSkin = this.layoutService.skin;
   * console.log('Current skin:', currentSkin);
   * ```
   */
  public get skin(): IThemeSkin {
    return this._layoutConfig.value.skin as IThemeSkin;
  }

  /**
   * @description Setter for theme skin. Changes the visual appearance of the application.
   * Includes validation to prevent unnecessary updates.
   * 
   * @param {IThemeSkin} skin - The theme skin to apply (e.g., ThemeSkin.Default, ThemeSkin.Bordered)
   * 
   * @example
   * ```typescript
   * // Apply bordered skin
   * this.layoutService.skin = ThemeSkin.Bordered;
   * 
   * // Apply default skin
   * this.layoutService.skin = ThemeSkin.Default;
   * ```
   */
  public set skin(skin: IThemeSkin) {
    
    // ES6 Destruction
    const { value: { skin: currentSkinStatus} } = this._layoutConfig;

    if (skin == null || skin == currentSkinStatus) {
      return;
    }

    this.storeLayoutConfigChanges({skin});

  }  

  /**
   * @description Getter for the current RTL (Right-to-Left) mode status.
   * 
   * @returns {boolean} True if RTL mode is enabled, false otherwise
   * 
   * @example
   * ```typescript
   * if (this.layoutService.rtlMode) {
   *   console.log('RTL mode is active');
   * }
   * ```
   */
  public get rtlMode(): boolean {
    return this._layoutConfig.value.isRtl ? true : false;
  }

  /**
   * @description Setter for RTL mode. Enables or disables right-to-left text direction,
   * commonly used for languages like Arabic and Hebrew.
   * 
   * @param {boolean} isRtl - True to enable RTL mode, false to disable
   * 
   * @example
   * ```typescript
   * // Enable RTL for Arabic language
   * this.layoutService.rtlMode = true;
   * ```
   */
  public set rtlMode(isRtl: boolean) {

    // ES6 Destruction
    const { value: { isRtl: currentRtlStatus } } = this._layoutConfig;

    if (typeof isRtl != "boolean" || isRtl == currentRtlStatus) {
      return;
    }

    this.storeLayoutConfigChanges({isRtl});

  }

  /**
   * @description Getter for the current menu collapsed status.
   * 
   * @returns {boolean} True if the menu is collapsed, false if expanded
   * 
   * @example
   * ```typescript
   * if (this.layoutService.menuCollapsed) {
   *   console.log('Menu is collapsed');
   * }
   * ```
   */
  public get menuCollapsed(): boolean {
    return this._layoutConfig.value.menuCollapsed ? true : false;
  }

  /**
   * @description Setter for menu collapsed state. Controls whether the navigation menu
   * is collapsed (minimized) or expanded.
   * 
   * @param {boolean} menuCollapsed - True to collapse the menu, false to expand
   * 
   * @example
   * ```typescript
   * // Collapse the menu
   * this.layoutService.menuCollapsed = true;
   * 
   * // Expand the menu
   * this.layoutService.menuCollapsed = false;
   * ```
   */
  public set menuCollapsed(menuCollapsed: boolean) {
    
    // ES6 Destruction
    const { value: { menuCollapsed: currentMenuCollapsedStatus } } = this._layoutConfig;

    if (typeof menuCollapsed != "boolean" || menuCollapsed == currentMenuCollapsedStatus) {
      return;
    }

    this.storeLayoutConfigChanges({menuCollapsed});

  }

  /**
   * @description Toggles the current menu collapsed state. If the menu is currently collapsed,
   * it will be expanded, and vice versa. Automatically saves changes to localStorage.
   * 
   * @example
   * ```typescript
   * // Toggle menu between collapsed and expanded states
   * this.layoutService.toggleMenuCollapsed();
   * ```
   */
  public toggleMenuCollapsed(): void {

    // toggle menu collapsed
    this.menuCollapsed = !this.menuCollapsed;

    // update layout config
    // save changes in localStorage
    this.saveLayoutConfigChanges();
  }

  /**
   * @description Private method that updates the DOM data-bs-theme attribute to reflect
   * the current theme mode (light/dark). Only updates if the value has actually changed.
   * 
   * @private
   * @param {ILayoutMode} mode - The theme mode to apply ('light' or 'dark')
   * 
   * @example
   * ```typescript
   * // This method is called internally by the service
   * // External components should use the public darkMode setter instead
   * ```
   */
  private _changeThemeModeByDom(mode: ILayoutMode): void {

    const currentMode = this._document.documentElement.getAttribute('data-bs-theme') as ILayoutMode;

    if (currentMode == null || mode == null || currentMode == mode) {
      return;
    }

    this._document.documentElement.setAttribute('data-bs-theme', mode);

    console.log(`theme mode is changed to ${mode} ...`);

  }

  /**
   * @description Private method that applies semi-dark theme settings to the DOM.
   * Updates data-semidark-menu attribute and data-template attribute based on the setting.
   * 
   * @private
   * @param {boolean} enabledSemiDark - Whether to enable semi-dark theme
   * 
   * @example
   * ```typescript
   * // This method is called internally by the service
   * // External components should use the public enabledSemiDark setter instead
   * ```
   */
  private _applyThemeSemiDarkByDOM(enabledSemiDark: boolean): void {

    const html = this._document.documentElement;
    const value = String(enabledSemiDark).toString();
    const currentSemiDarkMode = html.getAttribute('data-semidark-menu');

    if (currentSemiDarkMode == null || currentSemiDarkMode == value) {
      return;
    }

    // update attribute-data
    html.setAttribute('data-semidark-menu', value);
    html.setAttribute('data-template', enabledSemiDark ? 'vertical-menu-template-semi-dark' : 'vertical-menu-template')

    console.log(enabledSemiDark ? 'theme semi dark is enabled ...' : 'theme semi dark is disabled ...');

  }

  /**
   * @description Private method that updates the DOM data-skin attribute to reflect
   * the current theme skin. Only updates if the value has actually changed.
   * 
   * @private
   * @param {IThemeSkin} skin - The theme skin to apply
   * 
   * @example
   * ```typescript
   * // This method is called internally by the service
   * // External components should use the public skin setter instead
   * ```
   */
  private _changeThemeSkinByDom(skin: IThemeSkin): void {

    const currentSkin = this._document.documentElement.getAttribute('data-skin') as IThemeSkin;

    if (currentSkin == null || skin == null || currentSkin.toString() == skin) {
      return;
    }

    this._document.documentElement.setAttribute('data-skin', skin);

    console.log(`theme skin is changed to ${skin} ...`);

  }  

  /**
   * @description Private method that updates the DOM dir attribute to reflect
   * the current text direction (LTR/RTL). Only updates if the value has actually changed.
   * 
   * @private
   * @param {ILayoutDirection} dir - The text direction to apply ('ltr' or 'rtl')
   * 
   * @example
   * ```typescript
   * // This method is called internally by the service
   * // External components should use the public rtlMode setter instead
   * ```
   */
  private _changeThemeDirectionByDom(dir: ILayoutDirection): void {

    const currentDir = this._document.documentElement.getAttribute('dir') as ILayoutDirection;

    if (currentDir == null || dir == null || currentDir == dir) {
      return;
    }

    this._document.documentElement.setAttribute('dir', dir);

    console.log(`theme direction is changed to ${dir} ...`);

  }  

  /**
   * @description Private method that updates the DOM lang attribute to reflect
   * the current language setting. Only updates if the value has actually changed.
   * 
   * @private
   * @param {ILayoutLang} lang - The language to apply ('en' or 'ar')
   * 
   * @example
   * ```typescript
   * // This method is called internally by the service
   * // Language changes should be handled by the language service
   * ```
   */
  private _changeThemeLangByDom(lang: ILayoutLang): void {

    const currentLang = this._document.documentElement.getAttribute('lang') as ILayoutLang;

    if (currentLang == null || lang == null || currentLang == lang) {
      return;
    }

    // update html lang
    this._document.documentElement.setAttribute('lang', lang);

    console.log(`theme lang is changed to ${lang} ...`);

  }  

  /**
   * @description Private method that adds or removes the 'layout-menu-collapsed' CSS class
   * from the document element based on the menu collapsed state.
   * 
   * @private
   * @param {boolean} menuCollapsed - Whether the menu should be collapsed
   * 
   * @example
   * ```typescript
   * // This method is called internally by the service
   * // External components should use the public menuCollapsed setter instead
   * ```
   */
  private _toggleThemeMenuCollapseByDom(menuCollapsed: boolean): void {

    // get html tag;
    const html = this._document.documentElement;
    const isCollapsed = html.classList.contains("layout-menu-collapsed");

    // if menuCollapsed is null or equal the previous then the func will skip execution
    if (menuCollapsed == null || menuCollapsed == isCollapsed) {
      return;
    }

    // add/remove class( layout-menu-collapsed )
    if (menuCollapsed && !isCollapsed) {
      html.classList.add("layout-menu-collapsed");
    }
    else if (!menuCollapsed && isCollapsed) {
      html.classList.remove("layout-menu-collapsed");
    } 

    console.log(menuCollapsed ? 'theme menu is collapsed ...' : 'theme menu is expanded ...');

  }

  /**
   * @description Toggles the 'layout-menu-expanded' CSS class on the document element.
   * This method is typically used for mobile menu expansion states.
   * 
   * @example
   * ```typescript
   * // Toggle mobile menu expansion
   * this.layoutService.toggleMenuExpanded();
   * ```
   */
  public toggleMenuExpanded(): void {

    // get html tag;
    const html = this._document.documentElement;
    html.classList.toggle("layout-menu-expanded");

    console.log('menu is expanded is toggled ...');

  }

  /**
   * @description Checks if the menu hover effect is currently active.
   * 
   * @returns {boolean} True if the menu hover class is present, false otherwise
   * 
   * @example
   * ```typescript
   * if (this.layoutService.isMenuHover()) {
   *   console.log('Menu is in hover state');
   * }
   * ```
   */
  public isMenuHover(): boolean {
    
    // get html tag;
    const html = this._document.documentElement;

    // check if the menu is hovered
    const isHover = html.classList.contains("layout-menu-hover");

    return isHover;
  }

  /**
   * @description Removes the menu hover effect by removing the 'layout-menu-hover' CSS class
   * from the document element. Only removes the class if it's currently present.
   * 
   * @example
   * ```typescript
   * // Remove menu hover effect
   * this.layoutService.removeMenuHover();
   * ```
   */
  public removeMenuHover(): void {
    
    if (!this.isMenuHover()) {
      return;
    }

    // get html tag;
    const html = this._document.documentElement;

    // remove the menu hover class
    html.classList.remove("layout-menu-hover");

    // log the action
    console.log('menu hover is removed ...');

  }

  /**
   * @description Adds the menu hover effect by adding the 'layout-menu-hover' CSS class
   * to the document element. Only adds the class if it's not currently present.
   * 
   * @example
   * ```typescript
   * // Add menu hover effect
   * this.layoutService.addMenuHover();
   * ```
   */
  public addMenuHover(): void {

    if (this.isMenuHover()) {
      return;
    }

    // get html tag;
    const html = this._document.documentElement;

    // add the menu hover class
    html.classList.add("layout-menu-hover");
    
    // log the action
    console.log('menu hover is added ...');
  }

  /**
   * @description Toggles the 'layout-menu-hover' CSS class on the document element.
   * This method is typically used for menu hover effects and interactions.
   * 
   * @example
   * ```typescript
   * // Toggle menu hover effects
   * this.layoutService.toggleMenuHover();
   * ```
   */
  public toggleMenuHover(): void {
    this.isMenuHover() ? this.removeMenuHover() : this.addMenuHover();
  }

  /**
   * @description Stores layout configuration changes in the internal configuration object.
   * This method merges new configuration values with existing ones using the spread operator.
   * 
   * @param {ILayoutConfig} newLayoutConfig - Partial configuration object with new values
   * 
   * @example
   * ```typescript
   * // Store multiple configuration changes at once
   * this.layoutService.storeLayoutConfigChanges({
   *   isDark: true,
   *   isRtl: false,
   *   lang: LayoutLang.En
   * });
   * ```
   */
  public storeLayoutConfigChanges(newLayoutConfig: ILayoutConfig): void {
    
    // spread old value then update it
    this._defaultLayoutConfig = {...this._defaultLayoutConfig, ...newLayoutConfig};

  }
  
  /**
   * @description Emits the current layout configuration through the BehaviorSubject,
   * notifying all subscribers of configuration changes. This method is typically
   * called after storing configuration changes to trigger DOM updates.
   * 
   * @example
   * ```typescript
   * // Store changes and then emit them
   * this.layoutService.storeLayoutConfigChanges({ isDark: true });
   * this.layoutService.saveLayoutConfigChanges();
   * ```
   */
  public saveLayoutConfigChanges(): void {
    this._layoutConfig.next(this._defaultLayoutConfig)
  }

  /**
   * @description Private initialization method that sets up the subscription to layout
   * configuration changes and applies the current configuration to the DOM. This method
   * is called during service construction to ensure the initial state is properly applied.
   * 
   * @private
   * 
   * @example
   * ```typescript
   * // This method is called automatically during service construction
   * // No external calls are needed
   * ```
   */
  private _initConfig() {

    this.layoutConfig$
        .subscribe({
          next: currentConfig => {

            // theme lang
            this._changeThemeLangByDom(currentConfig.lang as ILayoutLang);

            // semi dark
            this._applyThemeSemiDarkByDOM(!!currentConfig.enabledSemiDark);

            // menu collapse
            this._toggleThemeMenuCollapseByDom(!!currentConfig.menuCollapsed);

            // theme mode
            this._changeThemeModeByDom(currentConfig.isDark ? LayoutMode.Dark : LayoutMode.Light);

            // theme direction
            this._changeThemeDirectionByDom(currentConfig.isRtl ? LayoutDirection.Rtl : LayoutDirection.Ltr);

            // theme skin
            this._changeThemeSkinByDom(currentConfig.skin as ThemeSkin);
            
            // save current statue in localStorage
            localStorage.setItem('layoutConfig', JSON.stringify(currentConfig));
            
            console.log("Layout Config Initialized...");
            
          },
          error: _ => console.error(_)
        })

  }

}
